/**
 * Custom List Item Node for Lexical
 * Extends ListItemNode with blockId support for AST compatibility
 */

import { ListItemNode, SerializedListItemNode } from '@lexical/list'
import { $applyNodeReplacement, DOMConversionMap, DOMExportOutput, EditorConfig, LexicalNode, Spread } from 'lexical'

export type SerializedCustomListItemNode = Spread<
  {
    blockId: string
    dir?: 'ltr' | 'rtl'
  },
  SerializedListItemNode
>

export class CustomListItemNode extends ListItemNode {
  __blockId: string

  static getType(): string {
    return 'custom-listitem'
  }

  static clone(node: CustomListItemNode): CustomListItemNode {
    const cloned = new CustomListItemNode(node.__blockId, node.__value, node.__checked, node.__key)
    cloned.__dir = node.__dir
    // Preserve indent level for nested list items
    cloned.__indent = node.__indent
    return cloned
  }

  constructor(blockId: string, value?: number, checked?: boolean, key?: string) {
    super(value, checked, key)
    this.__blockId = blockId
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getDir(): 'ltr' | 'rtl' | null {
    return this.__dir
  }

  setDir(dir: 'ltr' | 'rtl' | null): this {
    const writable = this.getWritable()
    writable.__dir = dir
    // Keep Lexical's standard direction in sync — see CustomParagraphNode.setDir.
    writable.setDirection(dir)
    return writable
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      dom.setAttribute('dir', this.__dir)
      dom.style.direction = this.__dir
      dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
    }
    // Apply indent styling on initial render (Lexical only does this in updateDOM)
    const indent = this.__indent
    if (indent > 0) {
      const indentSize = indent * 40 // Lexical's default indent size
      dom.style.setProperty('--lexical-indent', `${indentSize}px`)
      // Use margin instead of padding so bullet moves with text
      dom.style.marginInlineStart = `calc(32px + ${indentSize}px)`
    }
    return dom
  }

  updateDOM(prevNode: ListItemNode, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config)
    const prevCustom = prevNode as CustomListItemNode
    if (prevCustom.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevCustom.__dir !== this.__dir) {
      if (this.__dir) {
        dom.setAttribute('dir', this.__dir)
        dom.style.direction = this.__dir
        dom.style.textAlign = this.__dir === 'rtl' ? 'right' : 'left'
      } else {
        dom.removeAttribute('dir')
        dom.style.direction = ''
        dom.style.textAlign = ''
      }
    }
    // Update indent styling when indent changes
    if (prevCustom.__indent !== this.__indent) {
      const indent = this.__indent
      if (indent > 0) {
        const indentSize = indent * 40
        dom.style.setProperty('--lexical-indent', `${indentSize}px`)
        dom.style.marginInlineStart = `calc(32px + ${indentSize}px)`
        dom.style.paddingInlineStart = '' // Clear Lexical's default padding
      } else {
        dom.style.removeProperty('--lexical-indent')
        dom.style.marginInlineStart = ''
      }
    }
    return updated
  }

  static importDOM(): DOMConversionMap | null {
    return {
      li: () => ({
        conversion: (element: HTMLElement) => {
          const blockId = element.getAttribute('data-block-id') || ''
          const dir = element.getAttribute('dir') as 'ltr' | 'rtl' | null
          const node = $createCustomListItemNode(blockId)
          if (dir) {
            node.setDir(dir)
          }
          return { node }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('li')
    element.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      element.setAttribute('dir', this.__dir)
    }
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomListItemNode): CustomListItemNode {
    const node = $createCustomListItemNode(serializedNode.blockId)
    if (serializedNode.dir) {
      node.setDir(serializedNode.dir)
    }
    node.setValue(serializedNode.value)
    node.setChecked(serializedNode.checked)
    // Critical: set __indent directly since setIndent requires editor context
    const incomingIndent = serializedNode.indent ?? 0
    // Direct assignment works during importJSON when setIndent() doesn't
    ;(node as any).__indent = incomingIndent
    return node
  }

  exportJSON(): SerializedCustomListItemNode {
    const base = super.exportJSON()
    // Override indent from base since super.exportJSON() may not capture our __indent correctly
    return {
      ...base,
      type: 'custom-listitem',
      blockId: this.__blockId,
      dir: this.__dir || undefined,
      indent: this.__indent, // Explicitly override with our __indent
    }
  }
}

export function $createCustomListItemNode(blockId: string): CustomListItemNode {
  return $applyNodeReplacement(new CustomListItemNode(blockId))
}

export function $isCustomListItemNode(node: LexicalNode | null | undefined): node is CustomListItemNode {
  return node instanceof CustomListItemNode
}
